import { useEffect, useRef, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "../context/LoginContext";
import { logout } from "../apis/backend";

// Session timeout in milliseconds (default: 30 minutes)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// Events that count as "user activity"
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "touchstart",
    "click",
];

/**
 * Hook that automatically logs the user out after a period of inactivity.
 * It listens for mouse/keyboard/touch activity and resets the timer.
 * When the timer expires, it calls the logout API, clears local state,
 * and redirects to the home page.
 */
export default function useSessionTimeout() {
    const { isLoggedIn, setIsLoggedIn, setUser } = useContext(LoginContext);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();

    const performLogout = useCallback(async () => {
        console.warn("Session timed out due to inactivity. Logging out...");

        // Call backend logout
        try {
            await logout(navigate);
        } catch (e) {
            console.error("Logout API failed during session timeout:", e);
        }

        // Clear client-side state
        localStorage.removeItem("user");
        localStorage.removeItem("isLoggedIn");
        setIsLoggedIn(false);
        setUser({ email_id: "", role: undefined });

        // Redirect to home/login
        navigate("/", { replace: true });
    }, [setIsLoggedIn, setUser]);

    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(performLogout, SESSION_TIMEOUT_MS);
    }, [performLogout]);

    useEffect(() => {
        if (!isLoggedIn) return;

        // Start the inactivity timer
        resetTimer();

        // Reset timer on any user activity
        const handlers = ACTIVITY_EVENTS.map((event) => {
            const handler = () => resetTimer();
            window.addEventListener(event, handler, { passive: true });
            return { event, handler };
        });

        return () => {
            // Cleanup
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            handlers.forEach(({ event, handler }) => {
                window.removeEventListener(event, handler);
            });
        };
    }, [isLoggedIn, resetTimer]);
}
